/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useState } from 'react';
import { Check, ShieldCheck } from 'lucide-react';
import { api } from '../lib/api';
import { errorText } from '../lib/errors';
import { LABEL_POLICY_SCOPE, labelPolicyChoices, type LabelPolicy, type LabelPolicyStatus } from '../lib/governance';
import { Button, Callout, Icon, Panel, Skeleton, StatusChip } from '../ui';
import { SaveNote } from './SaveNote';

/**
 * Governance → Sensitivity labels: what Gyro does with Microsoft 365 content that carries a Purview sensitivity label
 * (GET/PUT /v1/admin/label-policy). Default allow-audit (owner ruling D6). An auditor can read it; the service refuses
 * an auditor's save, and the card shows that refusal.
 */
export function LabelPolicyCard() {
  const [status, setStatus] = useState<LabelPolicyStatus | null>(null);
  const [choice, setChoice] = useState<LabelPolicy>('allow-audit');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    let live = true;
    api.labelPolicy().then(
      (current) => {
        if (!live) return;
        setStatus(current);
        setChoice(current.policy);
      },
      (error) => {
        if (live) setLoadError(errorText(error, 'The sensitivity-label policy could not be read.'));
      },
    );
    return () => {
      live = false;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    setNote(null);
    try {
      const saved = await api.setLabelPolicy(choice);
      setStatus(saved);
      setChoice(saved.policy);
      setNote({ ok: true, text: 'Policy saved. It applies from the next question, and the change is in the audit trail.' });
    } catch (error) {
      setNote({ ok: false, text: errorText(error, 'The policy was not saved.') });
    } finally {
      setSaving(false);
    }
  };

  const choices = labelPolicyChoices(status?.releaseMinutes);
  return (
    <Panel
      title={<span className="ui-title-icon"><Icon icon={ShieldCheck} size="md" />Sensitivity labels</span>}
      description="What Gyro does with Microsoft 365 content that carries a Microsoft Purview sensitivity label"
      footer={(
        <div className="ui-foot-text">
          <p className="ui-para">{LABEL_POLICY_SCOPE.labelledLead}</p>
          <ul className="ui-list">{LABEL_POLICY_SCOPE.labelled.map((line) => <li key={line}>{line}</li>)}</ul>
          <p className="ui-para">{LABEL_POLICY_SCOPE.unlabelledLead}</p>
          <ul className="ui-list">{LABEL_POLICY_SCOPE.unlabelled.map((line) => <li key={line}>{line}</li>)}</ul>
        </div>
      )}
    >
      <div className="ui-form">
        {loadError ? (
          <Callout tone="bad" role="alert">{loadError}</Callout>
        ) : !status ? (
          <div className="ui-form" aria-hidden="true"><Skeleton size="lg" /><Skeleton size="lg" /><Skeleton size="lg" /></div>
        ) : (
          <fieldset className="ui-fieldset">
            <legend className="ui-field-label">Policy</legend>
            <div className="ui-radio-list">
              {choices.map((option) => (
                <label key={option.value} className="ui-radio">
                  <input
                    type="radio"
                    name="label-policy"
                    value={option.value}
                    checked={choice === option.value}
                    disabled={saving}
                    onChange={() => setChoice(option.value)}
                  />
                  <span className="ui-radio-text">
                    <span className="ui-radio-title">
                      <strong>{option.title}</strong>
                      {status.policy === option.value && <StatusChip tone="ok">current</StatusChip>}
                    </span>
                    <span className="ui-radio-desc">{option.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <div className="ui-actions">
          <Button variant="primary" icon={Check} busy={saving} disabled={!status || choice === status.policy} onClick={() => void save()}>Save policy</Button>
        </div>
        <SaveNote note={note} />
      </div>
    </Panel>
  );
}
